import { ClickAwayListener } from "@mui/base";
import Fade from "@mui/material/Fade";
import { usePopupState } from "material-ui-popup-state/hooks";
import type { FunctionComponent } from "react";
import { useCallback, useMemo } from "react";
import { Navigation } from "swiper/modules";

import { useMynewsFeedViewContext } from "scmp-app/components/my-news/feed-view/context";
import type { UserFollowedItem } from "scmp-app/components/my-news/feed-view/type";
import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";

import {
  Container,
  FollowedItem,
  PopupContainer,
  StyledIconSettings,
  StyledPopper,
  StyledSwiper,
  StyledSwiperSlide,
} from "./styles";
import { TopicFilteringPopup } from "./topic-filtering-popup";
import { TopicFilteringSelect } from "./topic-filtering-select";

export const TopicFiltering: FunctionComponent = () => {
  const { followedList, selectedItem, setSelectedItem } = useMynewsFeedViewContext();
  const popupId = "mynews-topic-filtering-popup";
  const popupState = usePopupState({ popupId, variant: "popper" });
  const modifiers = useMemo(
    () => [
      {
        enabled: false,
        name: "flip",
      },
      {
        enabled: false,
        name: "preventOverflow",
      },
    ],
    [],
  );

  const onItemClick = (item: UserFollowedItem) => {
    setSelectedItem(item);

    sendGA4Tracking({
      action: "click",
      category: "mynews",
      customized_parameters: {
        action_type: "select_section",
        follow_name: item.name,
        section_type: "topic",
      },
      subcategory: "menu",
    });
  };

  const onPopupClose = useCallback(() => {
    popupState.setOpen(false);
  }, [popupState]);

  if (followedList.length === 0) return null;
  return (
    <>
      <Container>
        <StyledSwiper
          modules={[Navigation]}
          navigation={true}
          pagination={false}
          slidesPerView="auto"
          spaceBetween={10}
        >
          <StyledSwiperSlide>
            <FollowedItem
              $isSelected={selectedItem.__typename === "all"}
              onClick={() =>
                onItemClick({
                  __typename: "all",
                  entityId: "",
                  entityUuid: "",
                  name: "All",
                })
              }
            >
              All
            </FollowedItem>
          </StyledSwiperSlide>
          {followedList.map(item => (
            <StyledSwiperSlide key={item.entityId}>
              <FollowedItem
                $isSelected={selectedItem.entityId === item.entityId}
                onClick={() => onItemClick(item)}
              >
                {item.name}
              </FollowedItem>
            </StyledSwiperSlide>
          ))}
        </StyledSwiper>

        <StyledPopper
          bindType="toggle"
          disablePortal={true}
          modifiers={modifiers}
          placement="bottom-end"
          popupComponent={({ TransitionProps }) => (
            <ClickAwayListener onClickAway={onPopupClose}>
              <Fade {...TransitionProps} timeout={200}>
                <PopupContainer>
                  <TopicFilteringPopup onClose={onPopupClose} />
                </PopupContainer>
              </Fade>
            </ClickAwayListener>
          )}
          popupId={popupId}
          popupState={popupState}
          transition={true}
          triggerComponent={<StyledIconSettings />}
        />
      </Container>
      <TopicFilteringSelect />
    </>
  );
};

TopicFiltering.displayName = "TopicFiltering";
